package org.example.project.game

import org.example.project.model.AttackCellState
import org.example.project.model.AttackType
import org.example.project.model.PlacedShip
import org.example.project.model.SerializedShip
import org.example.project.utils.AudioManager

object AttackEngine {

    fun targetCells(
        type: AttackType,
        row: Int,
        col: Int,
        lineHorizontal: Boolean = true
    ): List<Pair<Int, Int>> {
        val cells = mutableListOf<Pair<Int, Int>>()

        when (type) {
            AttackType.TORPEDO -> cells.add(row to col)

            AttackType.CROSS_FIRE -> {
                cells.add(row to col)
                cells.add(row - 1 to col)
                cells.add(row + 1 to col)
                cells.add(row to col - 1)
                cells.add(row to col + 1)
            }

            AttackType.CLUSTER_BOMB -> {
                for (r in row until row + 2) {
                    for (c in col until col + 2) {
                        cells.add(r to c)
                    }
                }
            }

            AttackType.LINE_STRIKE -> {
                if (lineHorizontal) {
                    for (c in 0 until 10) cells.add(row to c)
                } else {
                    for (r in 0 until 10) cells.add(r to col)
                }
            }

            AttackType.SONAR -> {
                for (r in row - 1..row + 1) {
                    for (c in col - 1..col + 1) {
                        cells.add(r to c)
                    }
                }
            }
        }

        return cells.filter { (r, c) -> r in 0 until 10 && c in 0 until 10 }
    }

    fun hasShipAt(
        ships: List<SerializedShip>,
        row: Int,
        col: Int
    ): Boolean =
        ships.any { ship ->
            val placed = ship.toPlacedShip()
            cellBelongsToShip(placed, row, col)
        }

    fun cellBelongsToShip(
        ship: PlacedShip,
        row: Int,
        col: Int
    ): Boolean {
        for (i in 0 until ship.type.size) {
            val r = if (ship.horizontal) ship.row else ship.row + i
            val c = if (ship.horizontal) ship.col + i else ship.col
            if (r == row && c == col) return true
        }
        return false
    }

    fun applyAttack(
        attackGrid: List<String>,
        opponentShips: List<SerializedShip>,
        type: AttackType,
        row: Int,
        col: Int,
        lineHorizontal: Boolean = true
    ): Pair<List<String>, Int> {
        val grid = if (attackGrid.size == 100) {
            attackGrid.toMutableList()
        } else {
            MutableList(100) { AttackCellState.UNKNOWN.name }
        }

        val cells = targetCells(type, row, col, lineHorizontal)
        val previouslySunk = opponentShips.count { ship ->
            isShipSunk(attackGrid, ship.toPlacedShip())
        }
        var newHits = 0

        for ((r, c) in cells) {
            val index = r * 10 + c
            val current = AttackCellState.valueOf(grid[index])

            if (type == AttackType.SONAR) {
                if (current == AttackCellState.UNKNOWN) {
                    grid[index] = if (hasShipAt(opponentShips, r, c)) {
                        AttackCellState.SCANNED_SHIP.name
                    } else {
                        AttackCellState.SCANNED_WATER.name
                    }
                }
                continue
            }

            if (current == AttackCellState.HIT || current == AttackCellState.MISS) {
                continue
            }

            if (hasShipAt(opponentShips, r, c)) {
                grid[index] = AttackCellState.HIT.name
                newHits++
            } else {
                grid[index] = AttackCellState.MISS.name
                AudioManager.playSplash()
            }
        }

        val nowSunk = opponentShips.count { ship ->
            isShipSunk(grid, ship.toPlacedShip())
        }
        val shipsSunk = nowSunk - previouslySunk

        return grid to (newHits + shipsSunk * 3)
    }

    fun isShipSunk(
        attackGrid: List<String>,
        ship: PlacedShip
    ): Boolean {
        for (i in 0 until ship.type.size) {
            val r = if (ship.horizontal) ship.row else ship.row + i
            val c = if (ship.horizontal) ship.col + i else ship.col
            val index = r * 10 + c
            if (AttackCellState.valueOf(attackGrid[index]) != AttackCellState.HIT) {
                return false
            }
        }
        return true
    }

    fun allShipsDestroyed(
        attackGrid: List<String>,
        ships: List<SerializedShip>
    ): Boolean =
        ships.all { isShipSunk(attackGrid, it.toPlacedShip()) }

    fun remainingShipCells(
        attackGrid: List<String>,
        ships: List<SerializedShip>
    ): Int {
        var count = 0
        for (ship in ships) {
            val placed = ship.toPlacedShip()
            for (i in 0 until placed.type.size) {
                val r = if (placed.horizontal) placed.row else placed.row + i
                val c = if (placed.horizontal) placed.col + i else placed.col
                val index = r * 10 + c
                if (AttackCellState.valueOf(attackGrid[index]) != AttackCellState.HIT) {
                    count++
                }
            }
        }
        return count
    }

    fun attackGridToBoardCells(
        attackGrid: List<String>,
        showUnknownAsEmpty: Boolean = true
    ): List<List<org.example.project.model.CellState>> {
        return List(10) { row ->
            List(10) { col ->
                val state = AttackCellState.valueOf(
                    attackGrid.getOrElse(row * 10 + col) {
                        AttackCellState.UNKNOWN.name
                    }
                )
                when (state) {
                    AttackCellState.HIT -> org.example.project.model.CellState.HIT
                    AttackCellState.MISS -> org.example.project.model.CellState.MISS
                    AttackCellState.SCANNED_WATER -> org.example.project.model.CellState.MISS
                    AttackCellState.SCANNED_SHIP -> org.example.project.model.CellState.SHIP
                    AttackCellState.UNKNOWN ->
                        if (showUnknownAsEmpty) {
                            org.example.project.model.CellState.EMPTY
                        } else {
                            org.example.project.model.CellState.EMPTY
                        }
                }
            }
        }
    }

    fun mergeMyBoardWithAttacks(
        myShips: List<PlacedShip>,
        attacksOnMe: List<String>
    ): org.example.project.model.Board {
        val board = org.example.project.model.Board()

        for (ship in myShips) {
            for (i in 0 until ship.type.size) {
                val r = if (ship.horizontal) ship.row else ship.row + i
                val c = if (ship.horizontal) ship.col + i else ship.col
                board.cells[r][c] = org.example.project.model.CellState.SHIP
            }
        }

        if (attacksOnMe.size == 100) {
            for (row in 0 until 10) {
                for (col in 0 until 10) {
                    when (AttackCellState.valueOf(attacksOnMe[row * 10 + col])) {
                        AttackCellState.HIT ->
                            board.cells[row][col] = org.example.project.model.CellState.HIT
                        AttackCellState.MISS ->
                            if (board.cells[row][col] != org.example.project.model.CellState.SHIP) {
                                board.cells[row][col] = org.example.project.model.CellState.MISS
                            }
                        else -> {}
                    }
                }
            }
        }

        return board
    }
}
